import { TranslationContext } from 'contexts/translation-context';
import { useEffect, useState, useContext } from 'react';
import styled from 'styled-components';
import { formatFloatWithCommasAndDecimals } from 'utils/new-formats';
import type { PackagePrices } from 'types/join';
import { getPlanName } from './formats';

const Container = styled.div`
  height: max-content;
  border: 1px solid ${props => props.theme.colors.red};
  border-radius: 5px;
  display: flex;
  flex-direction: column;
`;
const PlansContainer = styled.div`
  height: max-content;
  padding: 10px;
`;
const TotalContainer = styled.div`
  height: 37px;
  color: ${props => props.theme.colors.white};
  background-color: ${props => props.theme.colors.red};
  padding: 10px;
  font-family: ${props => props.theme.fonts.montserratBold};
  font-size: 16px;
  display: flex;
  justify-content: space-between;
`;
const Row = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 4px;
`;
const Text = styled.div`
  font-family: ${props => props.theme.fonts.montserratMedium};
  color: ${props => props.theme.colors.siteBlack};
  font-size: 16px;
`;
const Title = styled.div`
  text-align: center;
  font-size: 16px;
  font-family: ${props => props.theme.fonts.montserratBold};
  margin-bottom: 10px;
`;
export const OrderSummary = ({
  selectedPlan,
  selectedAddons
}: {
  selectedPlan: PackagePrices | undefined;
  selectedAddons: (PackagePrices | undefined)[] | undefined;
}) => {
  const TRANSLATION_TEXT = useContext(TranslationContext) as { [key: string]: string };
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let totalPrice = selectedPlan?.price ?? 0;
    selectedAddons?.forEach(addon => {
      totalPrice += (addon?.price ?? 0) * (addon?.addonCount ?? 0);
    });
    setTotal(totalPrice);
  }, [selectedPlan, selectedAddons]);

  const getAddonName = (addon: PackagePrices | undefined) => {
    if (addon === undefined) return '';
    if (addon.packageName === 'Member Addon') return `Additional User X ${addon.addonCount}`;
    if (addon.packageName === 'Rapaport Research Report add-on') return `Rapaport Research Report`;
    if (
      addon.packageName === 'Complementary Member' &&
      selectedAddons?.filter(a => a?.packageName === 'Complementary Member').length === 2
    )
      return 'Complimentary Members x 2';
    else return addon.packageName;
  };

  return (
    <>
      <Title>{TRANSLATION_TEXT.order_summary}</Title>
      <Container>
        <PlansContainer>
          <Row>
            <Text>{getPlanName(selectedPlan?.packageName)}</Text>
            <Text>${formatFloatWithCommasAndDecimals(selectedPlan?.price ?? 0)}</Text>
          </Row>
          {selectedAddons?.map(
            (addon, i) =>
              selectedAddons[i - 1]?.packageName !== addon?.packageName && (
                <Row key={addon?.packageName}>
                  <Text>{getAddonName(addon)}</Text>
                  <Text>
                    $
                    {addon?.price !== 0
                      ? formatFloatWithCommasAndDecimals(
                          (addon && addon.price * addon.addonCount) ?? 0
                        )
                      : 0}
                  </Text>
                </Row>
              )
          )}
        </PlansContainer>
        <TotalContainer>
          <div>{TRANSLATION_TEXT.total}</div>
          <div>${formatFloatWithCommasAndDecimals(total)}</div>
        </TotalContainer>
      </Container>
    </>
  );
};
